import React, { useState, useEffect, useCallback } from 'react';
import uuid4 from 'uuid/v4';
import _ from 'lodash';
import {
  Form, Input, Button, DatePicker, Typography, message, Icon, Collapse, Checkbox, Select, Popover, Badge, Tooltip, Skeleton, Alert,
  Row,
  Col,
  Modal,
} from 'antd';

import moment from 'moment';
import { axiosContext } from '../../../api/axios';

import { UNKNOWN_HTTP_ERROR_MESSAGE } from '../../../defaults';

import { watchCorrelationId } from '../../../actions/longpoller';

import { camelPreservePrivate, stripHints, hasNonEmptyObjectOrArray } from '../../../utils';
import getDateOfBirthField from '../../fields/DateOfBirthField';
import { IcrAttachmentModal } from './IcrAttachmentModal';

const { confirm } = Modal;

const BASIC_INFO_FIELDS = ['birthDate', 'email'];

export const SCAN_FORM_LAYOUT = {
  labelCol: { sm: { span: 6 }, md: { span: 6 } },
  wrapperCol: { sm: { span: 14 }, md: { span: 14 } },
};

function submitScan(type, json) {
  const allData = { ...json, correlationId: uuid4() };

  let finalValue = null;
  let sawNoop = false;
  const axiosInstance = axiosContext(localStorage.icrToken);
  let watchPromise;
  return new Promise((resolve, reject) => {
    const executeAsync = async () => {
      try {
        watchPromise = watchCorrelationId(allData.correlationId, (err, value) => {
          if (err) reject(err);
          const event = value && value.routingKey.split('.')[3];
          if (!event || !value) reject(new Error(UNKNOWN_HTTP_ERROR_MESSAGE));
          if (event === 'complete') {
            if (finalValue && finalValue.event === 'value') {
              resolve(JSON.parse(finalValue.content));
            } else if (finalValue && finalValue.event === 'error') {
              reject(new Error(JSON.parse(finalValue.content)));
            } else if (sawNoop) {
              // Scan was accepted but queued for async processing (only start +
              // noop emitted, no value/error). Resolve with a sentinel so the
              // caller can render a "queued" success state.
              resolve({ queued: true });
            } else {
              reject(new Error(UNKNOWN_HTTP_ERROR_MESSAGE));
            }
          } else if (event === 'value' || event === 'error') {
            finalValue = value;
            finalValue.event = event;
          } else if (event === 'noop') {
            sawNoop = true;
          }
        });
        await axiosInstance.post(`/api/scan/${type}`, allData);
      } catch (e) {
        console.error(e);
        if (watchPromise) {
          watchPromise.cancel();
        }
        if (e.response && e.response.data) {
          reject(new Error(e.response.data));
        } else {
          reject(new Error(UNKNOWN_HTTP_ERROR_MESSAGE));
        }
      }
    };
    executeAsync();
  });
}


const renderIcon = (schemaType, validationErrors, validationSuccess, targetInProgress, openPanels, groupCode, scanType) => {
  const isError = validationErrors[schemaType];
  const isSuccess = validationSuccess[schemaType];
  const isProgress = targetInProgress[schemaType];

  if (!isError && !isSuccess && !isProgress) {
    return openPanels[groupCode] === scanType ? <Icon type="edit" style={{ color: '#7CB9E8' }} /> : null;
  }

  if (!isSuccess && isProgress) {
    return <Icon type="loading" style={{ marginRight: '40px', marginTop: '6px', fontSize: '24px' }} />;
  }

  return (
    (isError && (
      <Icon
        type="exclamation-circle"
        theme="twoTone"
        twoToneColor="red"
        style={{ marginRight: '40px', marginTop: '10px', fontSize: '24px' }}
      />
    ))
    || (isSuccess && (
      <Icon
        type="check-circle"
        theme="twoTone"
        twoToneColor="#52c41a"
        style={{ marginRight: '40px', marginTop: '10px', fontSize: '24px' }}
      />
    ))
    || null
  );
};

export const CollapsePanelHeader = ({ title, icon }) => (
  <div style={{ display: 'flex', fontSize: '18px', alignItems: 'center', justifyContent: 'space-between' }}>
    <Typography.Title level={4} style={{ margin: '10px', color: '#9C6756' }}>
      {title}
    </Typography.Title>
    <div> {icon} </div>
  </div>
);


const ExistingAccredPopover = ({ form, scanType, existingAccred }) => {
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [selectedIdentifier, setSelectedIdentifier] = useState('');

  const handleClick = () => {
    form.setFieldsValue({ [`${scanType}.identifier`]: selectedIdentifier });
    setPopoverVisible(false);
  };


  const content = (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Select style={{ width: '220px' }} placeholder="accreditations" onChange={value => setSelectedIdentifier(value)}>
        {
          existingAccred.map(item => (
            <Select.Option key={item.identifier}>
              <Badge color={item.statusColor} />
              <Tooltip title={`${item.statusFlags.join()}`}>{item.identifier}</Tooltip>
            </Select.Option>
          ))
        }
      </Select>
      <Button type="primary" onClick={handleClick} style={{ margin: '10px' }}>
        Apply
      </Button>
    </div>
  );

  return (<Popover
    placement="rightBottom"
    content={content}
    title={`Existing ${scanType} accreditation(s)`}
    trigger="click"
    visible={popoverVisible}
    onVisibleChange={visible => setPopoverVisible(visible)}
          >
    <Icon type="select" style={{ color: '#82412C' }} />
  </Popover>);
};


export const IcrFormComponent = Form.create()(props => {
  const { tokenInfo, isPreviewMode, mockData } = props;
  const [fieldInfo, setFieldInfo] = useState({});
  const [groupInfo, setGroupInfo] = useState({});
  const [initLoading, setInitLoading] = useState(true);
  const [initApiError, setInitApiError] = useState(null);
  const [inProgress, setInProgress] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [validationSuccess, setValidationSuccess] = useState({});
  const [targetInProgress, setTargetInProgress] = useState({});
  const [constituent, setConstituent] = useState(null);
  const [openPanels, setOpenPanels] = useState({});
  const [nextActionEnabled, setNextActionEnabled] = useState(false);
  const [attachmentModalVisible, setAttachmentModalVisible] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [activePanelData, setActivePanelData] = useState({ icrcId: null, typeName: '' });
  const [skippedGroups, setSkippedGroups] = useState({});

  const fetchAttachments = useCallback(async () => {
    try {
      const axiosInstance = axiosContext(localStorage.icrToken);
      const response = await axiosInstance({
        method: 'get',
        url: `/api/info_capture_request/${props.tokenInfo.icrId}/attachments`,
      });
      setAttachments(response.data);
    } catch (error) {
      setAttachments([]);
    }
  }, [props.tokenInfo.icrId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let data;

        if (isPreviewMode && mockData) {
          // Use provided mock data for preview
          data = mockData;
        } else {
          // Fetch from API
          const apiUrl = '/api/icr/scan_types';
          const axiosInstance = axiosContext(localStorage.icrToken);
          const response = await axiosInstance.get(apiUrl, { params: { required_only: true } });
          data = response.data;
        }

        const { scanInfo, constituent, groupInfo, locationInfo } = data;
        Object.entries(scanInfo).forEach(([type, schemaInfo]) => {
          schemaInfo.fieldOrder = schemaInfo.fieldOrder
            .map(i => camelPreservePrivate(i))
            .filter(i => !BASIC_INFO_FIELDS.includes(i));
        });
        const sortedGroupInfo = Object.entries(groupInfo)
          .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
          .reduce((acc, [key, value]) => {
            acc[key] = {
              ...value,
              groupMembers: value.groupMembers.sort((a, b) => a.localeCompare(b)),
            };
            return acc;
          }, {});
        setFieldInfo(scanInfo);
        setGroupInfo(sortedGroupInfo);

        const stateKey = locationInfo.state ? `${locationInfo.state.toLowerCase()}wwc` : null;

        const defaultOpenPanels = Object.keys(groupInfo).reduce((acc, groupCode) => {
          const { groupMembers } = groupInfo[groupCode];
          const stateMember = stateKey && groupMembers.includes(stateKey) ? stateKey : groupMembers[0];
          acc[groupCode] = stateMember;
          return acc;
        }, {});
        setOpenPanels(defaultOpenPanels);
        setConstituent(constituent);
        setInitLoading(false);
      } catch (error) {
        message.error(UNKNOWN_HTTP_ERROR_MESSAGE);
        setInitApiError(error.message);
        setInitLoading(false);
        console.error('Error fetching field information:', error);
      }
    };

    const checkNextAction = async () => {
      try {
        const apiUrl = `/api/info_capture_request/${tokenInfo.icrId}/next_actions`;
        const axiosInstance = axiosContext(localStorage.icrToken);
        const { data } = await axiosInstance.get(apiUrl);
        const nextActionBool = hasNonEmptyObjectOrArray(data);
        setNextActionEnabled(nextActionBool);
      } catch (error) {
        console.error('Error fetching next action information');
      }
    };

    fetchData();
    if (!isPreviewMode) {
      checkNextAction();
      fetchAttachments();
    }
  }, [tokenInfo.icrId, fetchAttachments, isPreviewMode, mockData]);

  const disablePastDate = current => current && current < moment().endOf('day');

  const disableAttainmentDate = current => current && current > moment().endOf('day');

  const renderVehicleDeclarationField = () => {
    return (
      <>
        <div style={{ textAlign: 'left' }}>
          <p style={{ margin: '0 0 10px 0', lineHeight: '1.6', textAlign: 'left' }}>
            I acknowledge that I have elected to use my personal vehicle for work-related duties without holding or providing comprehensive motor vehicle insurance details.
          </p>
          <p style={{ margin: '0 0 8px 0', lineHeight: '1.6', textAlign: 'left' }}>
            I understand and agree to the following:
          </p>
          <ol style={{ margin: '0 0 10px 0', paddingLeft: '20px', lineHeight: '1.6', textAlign: 'left' }}>
            <li><strong>Assumption of Risk:</strong> I accept full responsibility for any financial loss, damage, or inconvenience resulting from an incident involving my vehicle, including but not limited to theft, collision, or write-off.</li>
            <li><strong>No Liability to APM:</strong> I waive any right to claim against APM (the Company) for compensation, reimbursement, or replacement of my vehicle or related costs arising from its use for work purposes.</li>
            <li><strong>Work Continuity:</strong> I confirm that I have made reasonable arrangements to ensure minimal disruption to my work duties should my vehicle become unavailable.</li>
            <li><strong>Compliance with Law:</strong> I acknowledge that I am responsible for ensuring that I hold a valid and current drivers' licence, my vehicle is roadworthy, registered, and compliant with all applicable Australian road safety laws and regulations.</li>
            <li><strong>Indemnity:</strong> Other than to the extent prohibited by law, I agree to indemnify and hold harmless APM from any claims, liabilities, or expenses arising from my use of an uninsured personal vehicle for work purposes.</li>
          </ol>
        </div>
      </>
    );
  };

  const renderFormField = (fieldName, schemaType, fieldInfo) => {
    const { getFieldDecorator, getFieldValue } = props.form;

    const openedTypes = Object.values(openPanels);
    const isEnabled = openedTypes.includes(schemaType);

    // Check if this field's group is skipped
    const isFieldInSkippedGroup = () => {
      for (const [groupCode, groupInformation] of Object.entries(groupInfo)) {
        if (groupInformation.groupMembers.includes(schemaType) && skippedGroups[groupCode]) {
          return true;
        }
      }
      return false;
    };

    let fieldElem = null;
    let itemLayout;

    const disableScan = validationSuccess[schemaType] || !isEnabled || (fieldInfo.default && !fieldInfo.editableDefault);
    const getValueFromEvent = null;

    if (!fieldInfo) return null;
    switch (fieldInfo.dataType) {
      case 'String':
      case 'PhoneNumberField': {
        let extraProps;
        if (fieldName === 'identifier' && fieldInfo.identifierSample) {
          extraProps = {
            ...extraProps,
            placeholder: fieldInfo.identifierSample,
          };
        }
        fieldElem = <Input disabled={disableScan} {...extraProps} />;
        break;
      }
      case 'DropdownField':
        if (!fieldInfo.options && !fieldInfo.options.length) fieldElem = <Input disabled={disableScan} />;
        else {
          fieldElem = (
            <Select showSearch disabled={disableScan}>
              {
                fieldInfo.options.map(value => <Select.Option key={value} value={value}>{value}</Select.Option>)
              }
            </Select>
          );
        }
        break;
      case 'Integer':
        fieldElem = <Input type="number" disabled={disableScan} />;
        break;
      case 'DateField':
        fieldElem = <DatePicker disabled={disableScan} format="DD-MM-YYYY" placeholder={'DD-MM-YYYY'} />;
        itemLayout = {
          labelCol: { xs: { span: 14 }, sm: { span: 6 } },
          wrapperCol: { xs: { span: 24 }, sm: { span: 6 } },
        };
        break;
      case 'ConsentField': {
        const consentDescription = fieldInfo.description || 'You must agree to the terms and conditions to proceed.';
        fieldElem = (<div style={{ display: 'flex', maxWidth: '720px' }}>
          <Checkbox disabled={disableScan} />
          <div style={{
            alignSelf: 'center', textAlign: 'left', marginLeft: '10px', fontWeight: 'bold', lineHeight: '1',
          }}
          >
            <span>{consentDescription}</span>
          </div>
        </div>);
        break;
      }
      case 'CheckBoxField':
        fieldElem = (<div style={{ display: 'flex', maxWidth: '720px' }}>
          <Checkbox disabled={disableScan}/>
        </div>);
        break;
      case 'VehicleConsentField':
        const vehicleConsentDescription = fieldInfo.description || 'You must agree to the terms and conditions to proceed.';
        fieldElem = (
          <div style={{ display: 'flex', maxWidth: '720px' }}>
            <Checkbox disabled={disableScan} />
            <div style={{
              alignSelf: 'center', textAlign: 'left', marginLeft: '10px', fontWeight: 'bold', lineHeight: '1',
            }}
            >
              <span>{vehicleConsentDescription}</span>
            </div>
          </div>
        );
        break;
      case 'AttainmentDateField':
        fieldElem = (
          <DatePicker
            disabled={disableScan}
            disabledDate={disableAttainmentDate}
            format="DD-MM-YYYY"
            placeholder={'DD-MM-YYYY'}
          />
        );
        itemLayout = {
          labelCol: { xs: { span: 14 }, sm: { span: 6 } },
          wrapperCol: { xs: { span: 24 }, sm: { span: 6 } },
        };
        break;
      case 'ExpiryDateField':
        fieldElem = (
          <DatePicker
            disabled={disableScan}
            disabledDate={disablePastDate}
            format="DD-MM-YYYY"
            placeholder={'DD-MM-YYYY'}
          />
        );
        itemLayout = {
          labelCol: { xs: { span: 14 }, sm: { span: 6 } },
          wrapperCol: { xs: { span: 24 }, sm: { span: 6 } },
        };
        break;
      default:
        fieldElem = (
          <Input disabled={true} placeholder={`Unsupported type: ${fieldInfo.dataType}`} />
        );
    }

    let defaultVal = '';
    if (fieldInfo.defaultValue !== null) defaultVal = fieldInfo.defaultValue;
    if (fieldInfo.default !== null) defaultVal = fieldInfo.default;

    const fieldDecoratorOptions = {
      initialValue: defaultVal,
      rules: [
        {
          required: isEnabled && !fieldInfo.ignoreRequired && !isFieldInSkippedGroup() ? fieldInfo.required : false,
          message: fieldInfo.validationMessage || `Please enter ${fieldName}`,
        },
      ],
    };

    if (fieldName === 'identifier' && fieldInfo.identifierPattern) {
      const patternString = fieldInfo.identifierPattern.replace(/^\/|\/$/g, '');
      const patternRule = {
        pattern: new RegExp(patternString, 'i'),
        message: `Please enter a valid identifier. ${fieldInfo.identifierSample ? `The Identifier should be in following format ${fieldInfo.identifierSample}` : ''}`, // Customize the message as needed
      };
      fieldDecoratorOptions.rules.push(patternRule);
    }

    if (getValueFromEvent) {
      fieldDecoratorOptions.getValueFromEvent = getValueFromEvent;
    }

    const fieldLabel = fieldInfo.label || _.startCase(fieldName);

    if (fieldName === 'fullName') {
      return (
        <>
          {getFieldDecorator(`${schemaType}.diffLegalName`, {
            valuePropName: 'checked',
            initialValue: false,
          })(
            <Checkbox>My legal name (including any middle names) appears differently on this document</Checkbox>,
          )}
          {getFieldValue(`${schemaType}.diffLegalName`) && (
            <Form.Item label={fieldLabel} key={`${schemaType}.${fieldName}`} style={{ marginTop: '10px' }}>
              {getFieldDecorator(`${schemaType}.${fieldName}`, fieldDecoratorOptions)(fieldElem)}
            </Form.Item>
          )}
        </>
      );
    }

    // Additional rendering for VehicleConsentField to have checkbox and declaration text at the bottom.
    if (fieldInfo.dataType === 'VehicleConsentField') {
      return (
        <>
          {renderVehicleDeclarationField()}
          <Form.Item label={fieldLabel} key={`${schemaType}.${fieldName}`} {...itemLayout} style={{ marginTop: '10px' }}>
            {getFieldDecorator(`${schemaType}.${fieldName}`, fieldDecoratorOptions)(fieldElem)}
          </Form.Item>
        </>
      );
    }

    return (
      <Form.Item label={fieldLabel} key={`${schemaType}.${fieldName}`} {...itemLayout} style={{ marginTop: '10px' }}>
        {getFieldDecorator(`${schemaType}.${fieldName}`, fieldDecoratorOptions)(fieldElem)}
      </Form.Item>
    );
  };

  const renderBasicFormFields = () => {
    const { getFieldDecorator } = props.form;
    const birthDate = (constituent && constituent.birthDate) ? moment(constituent.birthDate) : null;

    return (
      <div style={{ border: '1px solid #C88242', marginBottom: '30px' }}>
        <div style={{ textAlign: 'left', borderBottom: '1px solid #C88242' }}>
          <Typography.Title level={3} style={{ background: '#f5ece4', color: '#82412C', padding: '8px', margin: 0 }}>
            Basic Information
          </Typography.Title>
        </div>
        <div style={{ padding: '20px' }}>
          <Form.Item label={'Full Name'} key={'fullName'} style={{ marginTop: '10px' }}>
            {getFieldDecorator('basicInfo.fullName', {
              initialValue: constituent && constituent.fullName && stripHints(constituent.fullName) || '',
              rules: [
                {
                  required: true,
                  message: 'Please enter Full Name',
                },
              ],
            })(<Input disabled />)}
          </Form.Item>
          <Form.Item label={'Email'} key={'email'}>
            {getFieldDecorator('basicInfo.email', {
              initialValue: constituent && constituent.email || '',
              rules: [
                {
                  required: true,
                  message: 'Please enter Phone Number',
                },
              ],
            })(<Input disabled />)}
          </Form.Item>
          <Form.Item label={'Birth Date'} key={'birthDate'} labelCol={{ xs: { span: 14 }, sm: { span: 6 } }} wrapperCol={{ xs: { span: 24 }, sm: { span: 6 } }} >
            {getFieldDecorator('basicInfo.birthDate', {
              initialValue: birthDate || '',
              rules: [
                {
                  required: true,
                  message: 'Please enter Birth Date',
                },
              ],
            })(getDateOfBirthField({ initialValue: birthDate, yearsToRestrict: 10, disabled: !!birthDate }))}
          </Form.Item>
        </div>
      </div>
    );
  };

  const onFinish = e => {
    e.preventDefault();

    // Prevent submission in preview mode
    if (isPreviewMode) {
      return;
    }

    // Helper function to check if a scan type belongs to a skipped group
    const isInSkippedGroup = scanType => {
      for (const [groupCode, groupInformation] of Object.entries(groupInfo)) {
        if (groupInformation.groupMembers.includes(scanType) && skippedGroups[groupCode]) {
          return true;
        }
      }
      return false;
    };

    // Get all field names that belong to skipped groups
    const skippedFields = [];
    Object.entries(groupInfo).forEach(([groupCode, groupInformation]) => {
      if (skippedGroups[groupCode]) {
        groupInformation.groupMembers.forEach(scanType => {
          // Add all field names for this scan type
          if (fieldInfo[scanType] && fieldInfo[scanType].fieldOrder) {
            fieldInfo[scanType].fieldOrder.forEach(fieldName => {
              skippedFields.push(`${scanType}.${fieldName}`);
            });
          }
        });
      }
    });

    props.form.validateFieldsAndScroll(async (err, values) => {
      await fetchAttachments();

      // Filter out errors from skipped groups
      if (err) {
        const filteredErrors = Object.keys(err).filter(key => !skippedFields.includes(key));
        if (filteredErrors.length > 0) {
          message.error('Please address the validation issues below');
          return;
        }
      }

      const groupAttachmentsCount = attachments.reduce((acc, item) => {
        const { code } = item.screeningGroup;
        acc[code] = (acc[code] || 0) + 1;
        return acc;
      }, {});

      let response = true;
      for (const [groupCode, groupInformation] of Object.entries(groupInfo)) {
        // Skip attachment validation for skipped groups
        if (skippedGroups[groupCode]) continue;
        if (!groupInformation.minAttachmentCount) continue;
        if (!groupAttachmentsCount[groupCode] || groupInformation.minAttachmentCount > groupAttachmentsCount[groupCode]) {
          response = false;
          message.error(<span>Please provide the minimum attachments <strong style={{ color: '#C88242' }}>({groupInformation.minAttachmentCount})</strong> required for <strong style={{ color: '#C88242' }}>{groupInformation.groupName}</strong></span>);
          break;
        }
      }

      if (!response) return;

      const { basicInfo, ...scanInfo } = values;

      // Filter out disabled fields and fields from skipped groups
      const openedTypes = Object.values(openPanels);
      const filteredScanInfo = Object.keys(scanInfo).reduce((filtered, sectionName) => {
        if (!validationSuccess[sectionName] && openedTypes.includes(sectionName) && !isInSkippedGroup(sectionName)) {
          filtered[sectionName] = values[sectionName];
        }
        return filtered;
      }, {});

      setInProgress(true);

      try {
        const errorsByTarget = {};
        const successByTarget = {};
        await Promise.allSettled(
          Object.entries(filteredScanInfo).map(async ([schemaType, targetData]) => {
            try {
              const scanData = { ...basicInfo, ...targetData };
              const formattedScanData = {};
              Object.keys(scanData).forEach(key => {
                const value = scanData[key];
                if (moment.isMoment(value)) {
                  formattedScanData[key] = value.format('YYYY-MM-DD');
                } else {
                  formattedScanData[key] = value;
                }
              });
              setTargetInProgress(prevState => ({ ...prevState, [schemaType]: true }));
              const scanResponse = await submitScan(schemaType, { ...formattedScanData, constituent: { id: props.constituentId } });
              setTargetInProgress(prevState => ({ ...prevState, [schemaType]: false }));
              if (scanResponse && scanResponse.queued) {
                message.success('Scan queued. The result will appear once processed.');
                successByTarget[schemaType] = scanResponse;
                setValidationSuccess(successByTarget);
                return { type: 'success', scanResponse, schemaType };
              }
              const { found, current } = scanResponse.status;
              if (scanResponse._warnings) message.warning(scanResponse._warnings);
              if (!found || !current) {
                let messages = [];
                if (scanResponse.status.messages) {
                  messages = scanResponse.status.messages;
                } else if (scanResponse.status.found) {
                  messages = ['The provided details are not current'];
                } else {
                  messages = ['The provided details could not be verified'];
                }
                const error = { _errors: { search: messages } };
                errorsByTarget[schemaType] = error._errors;
                setValidationErrors(errorsByTarget);
                return { type: 'error', error, schemaType };
              }
              successByTarget[schemaType] = scanResponse;
              setValidationSuccess(successByTarget);
              return { type: 'success', scanResponse, schemaType };
            } catch (error) {
              console.error(error);
              errorsByTarget[schemaType] = error._errors;
              setValidationErrors(errorsByTarget);
              setTargetInProgress(prevState => ({ ...prevState, [schemaType]: false }));
              return { type: 'error', error, schemaType };
            }
          }),
        );

        setValidationSuccess(prevState => ({ ...prevState, ...successByTarget }));
        if (Object.keys(errorsByTarget).length === 0) {
          props.handleScanCompleted();
        }
      } catch (error) {
        console.error(error);
      } finally {
        setInProgress(false);
      }
    });
  };

  const handleCollapse = (groupCode, key) => {
    if (key) {
      setOpenPanels({ ...openPanels, [groupCode]: key });
    }
  };

  if (initLoading) {
    return <Skeleton />;
  }

  if (initApiError) {
    return <Alert type="error" message="Couldn't load form information" />;
  }

  const onAddAttachment = (groupCode, groupInfo) => {
    setAttachmentModalVisible(true);
    setActivePanelData({ icrcId: groupInfo.infoCaptureRequestCheckId, typeName: fieldInfo[openPanels[groupCode]].typeName, groupInfo, groupCode });
  };

  const handleDeleteAttachment = (id, index) => {
    confirm({
      title: 'Do you want to delete the attachment request?',
      content: <>This will result in deletion of <b>Attachment{index + 1}</b></>,
      async onOk() {
        try {
          const axiosInstance = axiosContext(localStorage.icrToken);
          await axiosInstance({
            method: 'delete',
            url: `/api/info_capture_request/${props.tokenInfo.icrId}/attachments/${id}`,
          });
          await fetchAttachments();
        } catch (error) {
          message.error('Something went wrong!');
        }
      },
      onCancel() {

      },
    });
  };

  const getAttachmentInfo = groupCode => {
    const filteredData = attachments.filter(item => item.screeningGroup.code === groupCode);
    if (!filteredData.length) return <p>No attachments available</p>;
    // eslint-disable-next-line consistent-return
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {
          filteredData.map((a, index) => (
            <Row>
              <Col span={20}>
                <a href={a.attachment.download} title={(a.attachment.filename && a.attachment.filename.length > 20) ? a.attachment.filename : ''} target='_blank' rel="noopener noreferrer">{
                  a.attachment.filename && a.attachment.filename.length < 20
                    ? a.attachment.filename
                    : `Attachment${index + 1}`
                }</a>
              </Col>
              <Col span={4}>
                {' '}<Icon type="close-circle" onClick={() => {
                  handleDeleteAttachment(a.attachment.id, index);
                }}
                     />
              </Col>
            </Row>
          ))
        }
      </div>
    );
  };

  const getCount = groupCode => {
    const filteredData = attachments.filter(item => item.screeningGroup.code === groupCode);
    return filteredData.length;
  };

  const addAttachmentButtonStatus = (groupInfo, groupCode) => {
    try {
      const currentAttachmentCount = getCount(groupCode);
      const { minAttachmentCount = 0, maxAttachmentCount } = groupInfo;
      if (minAttachmentCount > 0 && currentAttachmentCount < minAttachmentCount) return false;
      const effectiveMax = maxAttachmentCount ?? 5;
      return currentAttachmentCount >= effectiveMax;
    } catch (error) {
      return true;
    }
  };

  return (
    <>
      <div style={{ marginTop: '25px', width: '80%' }}>
        <Form
          onSubmit={onFinish}
          {...SCAN_FORM_LAYOUT}
        >
          <>
            {renderBasicFormFields()}
            {Object.entries(groupInfo).map(([groupCode, groupInfo]) => (
              <>
                <div style={{ border: '1px solid #C88242', marginBottom: '30px' }} >
                  <div style={{
                    display: 'flex', borderBottom: '1px solid #C88242', background: '#f5ece4', alignItems: 'center', justifyContent: 'space-between',
                  }}
                  >
                    <div style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'start', padding: '11px', margin: 0,
                    }}
                    >
                      <Typography.Title level={3} style={{ color: '#82412C' }}>
                        {groupInfo.groupName}
                      </Typography.Title>
                    </div>
                    {
                      groupInfo.minAttachmentCount > 0 || groupInfo.maxAttachmentCount
                        ? <div style={{
                          display: 'flex', paddingRight: '11px', alignItems: 'center', justifyContent: 'center',
                        }}
                          >
                          <Popover placement="right" trigger="click" content={getAttachmentInfo(groupCode)} title={<p>Attachments for <strong style={{ color: '#82412C' }}>{groupInfo.groupName}</strong></p>}>
                            <div style={{ display: 'flex', marginRight: '20px' }}>
                              <Badge count={getCount(groupCode)} style={{ backgroundColor: '#4B9CD3' }} >
                                <Icon style={{ display: 'flex', color: '#82412C', fontSize: '20px', cursor: 'pointer' }} type="file-protect" />
                              </Badge>
                            </div>
                          </Popover>
                          <Button disabled={addAttachmentButtonStatus(groupInfo, groupCode)} type="secondary" icon="plus" style={{ paddingRight: '30px' }} onClick={() => onAddAttachment(groupCode, groupInfo)}>Attachment</Button>
                        </div> : null
                    }
                  </div>
                  <Collapse accordion key={groupCode} activeKey={openPanels[groupCode]} bordered={true} onChange={key => handleCollapse(groupCode, key)} style={{ border: '1px solid #f5ece4', background: '#F5F5F5' }}>
                    {groupInfo.groupMembers.map(scanType => (
                      <Collapse.Panel key={scanType} header={<CollapsePanelHeader
                        title={fieldInfo[scanType].typeName}
                        icon={renderIcon(scanType, validationErrors, validationSuccess, targetInProgress, openPanels, groupCode, scanType)}
                                                             />} style={{ border: 'none', flexWrap: 'wrap', borderTop: '1px solid #f5ece4' }}
                      >
                        <div key={scanType}>
                          {fieldInfo[scanType].existingAccreditations && fieldInfo[scanType].existingAccreditations.length > 0 && <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <span style={{}}>Do you want to use existing information ?</span>
                            <div style={{ marginLeft: '10px' }}>
                              <ExistingAccredPopover form={props.form} scanType={scanType} existingAccred={fieldInfo[scanType].existingAccreditations} />
                            </div>

                          </div>}
                          {fieldInfo[scanType].guidingText && (
                            <div style={{ color: '#C88242', justifyContent: 'center', display: 'flex' }}>
                              <div key={fieldInfo[scanType]} style={{ maxWidth: '40vw', whiteSpace: 'pre-line' }}>
                                <strong>{fieldInfo[scanType].guidingText}</strong>
                              </div>
                            </div>
                          )}
                          {validationErrors[scanType] && (
                            <div style={{ color: 'red', justifyContent: 'center', display: 'flex' }}>
                              {Object.entries(validationErrors[scanType]).map(([key, errors]) => (
                                <div key={key} style={{ maxWidth: '40vw' }}>
                                  <strong>{key}: {errors}</strong>
                                </div>
                              ))}
                            </div>
                          )}
                          {fieldInfo[scanType].fieldOrder.map(fieldName => (
                            renderFormField(fieldName, scanType, fieldInfo[scanType].fields[fieldName])
                          ))}
                        </div>
                        <div>
                          <IcrAttachmentModal
                            visible={attachmentModalVisible}
                            constituentId={constituent.id}
                            infoCaptureRequestCheckId={activePanelData.icrcId}
                            activePanelTypeName={activePanelData.typeName}
                            icrId={tokenInfo.icrId}
                            groupData={{ groupInfo: activePanelData.groupInfo, groupCode: activePanelData.groupCode }}
                            isPreviewMode={isPreviewMode}
                            handleCancel={async () => {
                              setAttachmentModalVisible(false);
                              setActivePanelData({ icrcId: null, typeName: '' });
                              await fetchAttachments();
                            }}
                          />
                        </div>
                      </Collapse.Panel>
                    ))}
                  </Collapse>
                  {/* Show checkbox only if this specific group is marked as optional and no checks in this group have been successfully validated */}
                  {groupInfo.isOptional && !groupInfo.groupMembers.some(scanType => validationSuccess[scanType]) && (
                    <div style={{ padding: '15px', textAlign: 'left', borderTop: '1px solid #f0f0f0' }}>
                      <Checkbox
                        checked={skippedGroups[groupCode] || false}
                        onChange={e => {
                          setSkippedGroups({
                            ...skippedGroups,
                            [groupCode]: e.target.checked,
                          });
                        }}
                      >
                        I cannot supply the required information for any check above at this time
                      </Checkbox>
                    </div>
                  )}
                </div>
              </>
            ))}
          </>
          {!isPreviewMode && (
            <Button style={{ margin: '25px' }} type="primary" loading={inProgress} htmlType="submit">{nextActionEnabled ? 'Next' : 'Send'}</Button>
          )}
        </Form>
      </div>
    </>
  );
});
